import { useState, useEffect } from 'react';
import { X, Calendar, Users, Trophy, Clock, Plus, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import { supabase, authService } from '../lib/supabase';

interface Event {
    id: string;
    name: string;
    format: string;
    event_type: string;
    max_players: number;
    current_players: number;
    start_time: string;
    status: string;
    organizer_id: string;
}

interface Registration {
    id: string;
    event_id: string;
    events: {
        name: string;
        format: string;
        start_time: string;
        status: string;
    };
}

interface MyEventsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function MyEventsModal({ isOpen, onClose }: MyEventsModalProps) {
    const [user, setUser] = useState<{ id: string } | null>(null);
    const [hostedEvents, setHostedEvents] = useState<Event[]>([]);
    const [registeredEvents, setRegisteredEvents] = useState<Registration[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        const fetchData = async () => {
            setLoading(true);
            const currentUser = await authService.getCurrentUser();
            if (!currentUser) {
                setLoading(false);
                return;
            }
            setUser({ id: currentUser.id });

            // Fetch events I'm hosting
            const { data: hosted } = await supabase
                .from('events')
                .select('*')
                .eq('organizer_id', currentUser.id)
                .order('start_time', { ascending: true });

            if (hosted) setHostedEvents(hosted);

            // Fetch events I'm registered for
            const { data: registrations } = await supabase
                .from('event_registrations')
                .select('id, event_id, events(name, format, start_time, status)')
                .eq('player_id', currentUser.id);

            if (registrations) setRegisteredEvents(registrations as unknown as Registration[]);

            setLoading(false);
        };

        fetchData();
    }, [isOpen]);

    const handleDeleteEvent = async (eventId: string) => {
        if (!confirm('Delete this event? This cannot be undone.')) return;

        setDeleting(eventId);
        const { error } = await supabase.from('events').delete().eq('id', eventId);
        if (!error) {
            setHostedEvents(prev => prev.filter(e => e.id !== eventId));
        }
        setDeleting(null);
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0f172a] rounded-xl border border-white/20 p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-purple-400" />
                        My Events
                    </h2>
                    <button onClick={onClose} className="text-white/60 hover:text-white p-1">
                        <X className="size-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                    </div>
                ) : !user ? (
                    <div className="text-center py-12 text-white/40">
                        Please sign in to see your events.
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Events I'm Hosting */}
                        <div>
                            <h3 className="text-sm font-medium text-white/60 mb-3 flex items-center gap-2">
                                <Trophy className="w-4 h-4" />
                                Events I'm Hosting ({hostedEvents.length})
                            </h3>
                            {hostedEvents.length === 0 ? (
                                <div className="text-center py-6 text-white/30 bg-white/5 rounded-lg">
                                    You haven't created any events yet.
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {hostedEvents.map(event => (
                                        <div key={event.id} className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                                            <div>
                                                <div className="text-sm font-medium text-white">{event.name}</div>
                                                <div className="text-xs text-white/40">
                                                    {event.format} • {event.current_players}/{event.max_players} players • {formatDate(event.start_time)}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs px-2 py-0.5 rounded ${event.status === 'registration' ? 'bg-green-500/20 text-green-400' :
                                                        event.status === 'active' ? 'bg-purple-500/20 text-purple-400' :
                                                            event.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                                                                'bg-white/10 text-white/40'
                                                    }`}>
                                                    {event.status}
                                                </span>
                                                <button
                                                    onClick={() => handleDeleteEvent(event.id)}
                                                    disabled={deleting === event.id}
                                                    className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                                                >
                                                    {deleting === event.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Events I'm Registered For */}
                        <div>
                            <h3 className="text-sm font-medium text-white/60 mb-3 flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Events I'm Registered For ({registeredEvents.length})
                            </h3>
                            {registeredEvents.length === 0 ? (
                                <div className="text-center py-6 text-white/30 bg-white/5 rounded-lg">
                                    You haven't registered for any events yet.
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {registeredEvents.map(reg => (
                                        <div key={reg.id} className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                                            <div>
                                                <div className="text-sm font-medium text-white">{reg.events?.name}</div>
                                                <div className="text-xs text-white/40">
                                                    {reg.events?.format} • {formatDate(reg.events?.start_time)}
                                                </div>
                                            </div>
                                            <span className="flex items-center gap-1 text-xs text-green-400">
                                                <CheckCircle className="w-3 h-3" />
                                                Registered
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MyEventsModal;
